// Play visualization — manages which actions are drawn on the pitch map.
//
// DESIGN DECISION: The Angular app used a PlayVisualizationService with RxJS
// BehaviorSubjects. In React this is a useReducer-based hook because:
//  - The state is complex (category toggles → recompute elements → clear selection)
//    but local to the player view — no reason to put it in Redux.
//  - useReducer handles the chain of state transitions cleanly.
//  - The hook encapsulates all pitch viz logic; the view just passes results to PitchMap.
import type {
  ActionOutcome,
  PlayCategory,
  PlayerAction,
  PlayMetric,
  ReportingPlayer
} from '@frontend/core'
import type { UnitSystem } from '@frontend/core/lib/domains/units/types'
import { useCallback, useMemo, useReducer } from 'react'
import { isActionSuccessful, isVectorAction } from './action-utils'
import { formatStatValue, getColumnUnitLabel } from './format-cell'
import type { PlayCategoryDef } from './sport-config'

// Outcome bucketing priority for outcome-keyed categories. When a throw
// carries multiple outcomes (e.g. `Complete` + `Touchdown`) it should
// render once, under the strongest outcome. Higher index = higher priority.
const OUTCOME_PRIORITY: ActionOutcome[] = [
  'Complete',
  'Incomplete',
  'Interception',
  'Touchdown'
]

// Wording matches Match Tracker. Unlisted outcomes fall back to spaced words
// so a new backend value never renders as a raw token.
const SHOT_OUTCOME_LABELS: Partial<Record<ActionOutcome, string>> = {
  MissedWide: 'Missed Wide',
  MissedOverCrossbar: 'Missed over Crossbar',
  BlockedOrSaved: 'Blocked or Saved',
  HitPost: 'Hit Post'
}

function shotOutcomeLabel(outcome: ActionOutcome): string {
  return (
    SHOT_OUTCOME_LABELS[outcome] ?? outcome.replace(/([a-z])([A-Z])/g, '$1 $2')
  )
}

function dominantOutcome(
  action: PlayerAction,
  outcomeFilters: Set<ActionOutcome>
): ActionOutcome | null {
  if (!action.outcomes?.length) return null
  let best: ActionOutcome | null = null
  let bestRank = -1
  for (const o of action.outcomes) {
    if (!outcomeFilters.has(o)) continue
    const rank = OUTCOME_PRIORITY.indexOf(o)
    if (rank > bestRank) {
      best = o
      bestRank = rank
    }
  }
  return best
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PitchPoint {
  id: string
  x: number
  y: number
  category: PlayCategory
  isSuccess: boolean
  color: string
}

export interface PitchArrow extends PitchPoint {
  endX: number
  endY: number
}

interface PlayVisualizationState {
  selectedCategories: Set<PlayCategory>
  selectedElementId: string | null
  allCategories: PlayCategory[]
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

type Action =
  | { type: 'toggleCategory'; category: PlayCategory }
  | { type: 'selectElement'; id: string }
  | { type: 'resetSelection' }
  | { type: 'setAllCategories'; categories: PlayCategory[] }
  | { type: 'setCategories'; categories: Set<PlayCategory> }

function reducer(
  state: PlayVisualizationState,
  action: Action
): PlayVisualizationState {
  switch (action.type) {
    case 'toggleCategory': {
      const next = new Set(state.selectedCategories)
      if (next.has(action.category)) {
        next.delete(action.category)
      } else {
        next.add(action.category)
      }
      return { ...state, selectedCategories: next }
    }
    case 'selectElement':
      return { ...state, selectedElementId: action.id }
    case 'resetSelection':
      return { ...state, selectedElementId: null }
    case 'setAllCategories':
      return {
        ...state,
        allCategories: action.categories,
        selectedCategories: new Set(action.categories)
      }
    case 'setCategories':
      return { ...state, selectedCategories: action.categories }
  }
}

function initialState(): PlayVisualizationState {
  return {
    selectedCategories: new Set<PlayCategory>(),
    selectedElementId: null,
    allCategories: []
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UsePlayVisualizationOptions {
  /**
   * Roster players for the session — used to resolve passer / receiver
   * displayNames in the metrics strip. Safe to omit; receiver/player
   * rows just won't render.
   */
  players?: ReportingPlayer[]
  /**
   * Active unit system for the sport. Distances and speeds in the
   * metrics strip are converted + labelled through this, never via
   * hardcoded factors.
   */
  unitSystem?: UnitSystem
}

export function usePlayVisualization(
  actions: PlayerAction[],
  playCategories: PlayCategoryDef[],
  playerId?: string,
  options?: UsePlayVisualizationOptions
) {
  const { players, unitSystem } = options ?? {}
  const [state, dispatch] = useReducer(reducer, undefined, initialState)

  // Keep allCategories in sync with the provided definitions.
  const categoryKeys = playCategories.map((c) => c.category)
  const categoryKeysKey = categoryKeys.join(',')

  useMemo(() => {
    if (categoryKeysKey !== state.allCategories.join(',')) {
      dispatch({ type: 'setAllCategories', categories: categoryKeys })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryKeysKey])

  // Build points and arrows from actions + category definitions.
  const { points, arrows } = useMemo(() => {
    const pts: PitchPoint[] = []
    const arrs: PitchArrow[] = []

    // When any category declares an `outcomeFilter`, we're in outcome-keyed
    // mode (AMF/CFL QB-throw view): each action must render at most once,
    // under its dominant outcome. Outside of outcome mode categories are
    // independent (soccer) so an action can in principle match multiple
    // categories — we preserve that today by bucketing per-def.
    const outcomeDefs = playCategories.filter((d) => d.outcomeFilter)
    const outcomeMode = outcomeDefs.length > 0
    const outcomeFilterSet = new Set<ActionOutcome>(
      outcomeDefs.map((d) => d.outcomeFilter as ActionOutcome)
    )
    const defByOutcome = new Map<ActionOutcome, PlayCategoryDef>()
    for (const d of outcomeDefs) {
      if (d.outcomeFilter) defByOutcome.set(d.outcomeFilter, d)
    }

    const push = (def: PlayCategoryDef, action: PlayerAction) => {
      // An action with no recorded start coordinates cannot be plotted.
      if (action.x === null || action.y === null) return
      const success = isActionSuccessful(action)
      if (isVectorAction(action)) {
        arrs.push({
          id: action.id,
          x: action.x,
          y: action.y,
          endX: action.endX,
          endY: action.endY,
          category: def.category,
          isSuccess: success,
          color: def.color
        })
      } else {
        pts.push({
          id: action.id,
          x: action.x,
          y: action.y,
          category: def.category,
          isSuccess: success,
          color: def.color
        })
      }
    }

    const matchesScope = (a: PlayerAction, def: PlayCategoryDef) => {
      if (!def.actionTypes.includes(a.type)) return false
      if (def.filterField && playerId) {
        return a[def.filterField] === playerId
      }
      return true
    }

    if (outcomeMode) {
      // Single pass over actions — pick the dominant outcome per action.
      // Any category entry that also happens to lack `outcomeFilter` (mixed
      // config) still runs via the legacy per-def loop below.
      for (const action of actions) {
        // Use the first outcome-def whose scope (action type + player)
        // matches — all outcome-defs in AMF share the same scope today.
        const scopeDef = outcomeDefs.find((d) => matchesScope(action, d))
        if (!scopeDef) continue
        const outcome = dominantOutcome(action, outcomeFilterSet)
        if (!outcome) continue
        const def = defByOutcome.get(outcome)
        if (!def) continue
        push(def, action)
      }
    }

    for (const def of playCategories) {
      if (def.outcomeFilter) continue // handled above
      const matched = actions.filter((a) => matchesScope(a, def))
      for (const action of matched) push(def, action)
    }

    return { points: pts, arrows: arrs }
  }, [actions, playCategories, playerId])

  // Filter by selected categories.
  const visiblePoints = useMemo(
    () => points.filter((p) => state.selectedCategories.has(p.category)),
    [points, state.selectedCategories]
  )

  const visibleArrows = useMemo(
    () => arrows.filter((a) => state.selectedCategories.has(a.category)),
    [arrows, state.selectedCategories]
  )

  // The raw action for the selected element (if any)
  const selectedAction = useMemo<PlayerAction | null>(() => {
    if (!state.selectedElementId) return null
    return actions.find((a) => a.id === state.selectedElementId) ?? null
  }, [state.selectedElementId, actions])

  // Compute metrics for selected element or overall category summary.
  const metrics: PlayMetric[] = useMemo(() => {
    if (state.selectedElementId && selectedAction) {
      const allElements = [...points, ...arrows]
      const el = allElements.find((e) => e.id === state.selectedElementId)
      if (!el) return []

      // Unit-system-driven formatters. Distance/speed values flow through
      // the shared `formatStatValue` (convert + 1dp) so the tooltip matches
      // the tables, with the unit abbreviation appended inline. If no
      // unitSystem is supplied (e.g. tests), fall back to raw SI so the hook
      // stays safe to call without React context.
      const fmtDistance = (m_: number) =>
        unitSystem
          ? `${formatStatValue(m_, 'distance', unitSystem)} ${getColumnUnitLabel('distance', unitSystem)}`
          : `${m_.toFixed(1)} m`
      const fmtSpeed = (mps: number) =>
        unitSystem
          ? `${formatStatValue(mps, 'speed', unitSystem)} ${getColumnUnitLabel('speed', unitSystem)}`
          : `${mps.toFixed(1)} m/s`
      const fmtRevs = (rps: number) =>
        unitSystem
          ? `${formatStatValue(rps, 'revs', unitSystem)} ${getColumnUnitLabel('revs', unitSystem)}`
          : `${rps.toFixed(1)} rps`
      const fmtTime = (seconds: number) =>
        unitSystem
          ? `${formatStatValue(seconds, 'time', unitSystem)}${getColumnUnitLabel('time', unitSystem)}`
          : `${seconds.toFixed(1)}s`

      const playerName = (id?: string | null): string | null => {
        if (!id || !players) return null
        const p = players.find((pl) => pl.id === id)
        return p?.displayName ?? null
      }

      // AMF / CFL QB-throw strip: when the selected action is a pass with
      // an outcome-keyed category (set by the sport config), render the
      // rich per-throw metrics matching the Figma handoff.
      const isQbThrow =
        selectedAction.type === 'AmericanFootballPass' &&
        (el.category === 'Complete' ||
          el.category === 'Incomplete' ||
          el.category === 'Interception' ||
          el.category === 'Touchdown')

      if (isQbThrow) {
        const m: PlayMetric[] = []
        m.push({ title: 'Outcome', value: el.category })

        const passer = playerName(selectedAction.passingPlayerId)
        if (passer) m.push({ title: 'Player', value: passer })

        if (selectedAction.initialVelocity_mps != null) {
          m.push({
            title: 'Release Velocity',
            value: fmtSpeed(selectedAction.initialVelocity_mps)
          })
        }
        if (selectedAction.spin_rps != null) {
          m.push({
            title: 'Spin',
            value: fmtRevs(selectedAction.spin_rps)
          })
        }
        // Spiral efficiency is a 0–1 fraction; show it as a percentage. The slot is always
        // present (Figma layout) and falls back to '-' when the throw has no spiral value.
        m.push({
          title: 'Spiral',
          value:
            selectedAction.spiral != null
              ? `${(selectedAction.spiral * 100).toFixed(0)}%`
              : '-'
        })

        if (selectedAction.hangTime_s != null) {
          m.push({
            title: 'Hang Time',
            value: fmtTime(selectedAction.hangTime_s)
          })
        }
        if (selectedAction.distanceMeters != null) {
          m.push({
            title: 'Distance',
            value: fmtDistance(selectedAction.distanceMeters)
          })
        }
        if (selectedAction.timeToThrow_s != null) {
          m.push({
            title: 'Time to Throw',
            value: fmtTime(selectedAction.timeToThrow_s)
          })
        }
        if (selectedAction.territorialGain_m != null) {
          m.push({
            title: 'Field Yards Gained',
            value: fmtDistance(selectedAction.territorialGain_m)
          })
        }

        const receiver = playerName(selectedAction.receivingPlayerId)
        if (receiver) m.push({ title: 'Receiver', value: receiver })

        return m
      }

      // Generic (soccer) strip.
      const m: PlayMetric[] = [{ title: 'Category', value: el.category }]

      if (selectedAction.outcomes?.length) {
        if (el.category === 'Shot')
          m.push({
            title: 'Outcome',
            value: shotOutcomeLabel(selectedAction.outcomes[0])
          })
        else
          m.push({
            title: 'Retained / Lost',
            value: el.isSuccess ? 'Retained' : 'Lost'
          })
      }

      if (selectedAction.distanceMeters != null) {
        m.push({
          title: el.category === 'Carry' ? 'Carry Distance' : 'Distance',
          value: fmtDistance(selectedAction.distanceMeters)
        })
      }

      if (selectedAction.speedMps != null) {
        m.push({
          title: el.category === 'Carry' ? 'Carry Speed' : 'Speed',
          value: fmtSpeed(selectedAction.speedMps)
        })
      }

      if (selectedAction.direction && selectedAction.direction !== 'Unknown') {
        m.push({ title: 'Direction', value: selectedAction.direction })
      }

      return m
    }

    return playCategories
      .filter((def) => state.selectedCategories.has(def.category))
      .map((def) => {
        const catPoints = points.filter((p) => p.category === def.category)
        const catArrows = arrows.filter((a) => a.category === def.category)
        const total = catPoints.length + catArrows.length
        const successes =
          catPoints.filter((p) => p.isSuccess).length +
          catArrows.filter((a) => a.isSuccess).length
        return {
          title: def.label,
          value: total > 0 ? `${successes}/${total}` : '0'
        }
      })
  }, [
    state.selectedElementId,
    selectedAction,
    state.selectedCategories,
    points,
    arrows,
    playCategories,
    players,
    unitSystem
  ])

  // Callbacks
  const toggleCategory = useCallback(
    (cat: PlayCategory) => dispatch({ type: 'toggleCategory', category: cat }),
    []
  )

  const selectElement = useCallback(
    (id: string) => dispatch({ type: 'selectElement', id }),
    []
  )

  const resetSelection = useCallback(
    () => dispatch({ type: 'resetSelection' }),
    []
  )

  const toggleAll = useCallback(() => {
    const allSelected =
      state.selectedCategories.size === state.allCategories.length
    if (allSelected) {
      dispatch({ type: 'setCategories', categories: new Set() })
    } else {
      dispatch({
        type: 'setCategories',
        categories: new Set(state.allCategories)
      })
    }
  }, [state.selectedCategories.size, state.allCategories])

  const isCategorySelected = useCallback(
    (cat: PlayCategory) => state.selectedCategories.has(cat),
    [state.selectedCategories]
  )

  const selectedCount = state.selectedCategories.size

  return {
    state,
    points,
    arrows,
    visiblePoints,
    visibleArrows,
    metrics,
    selectedAction,
    toggleCategory,
    selectElement,
    resetSelection,
    toggleAll,
    isCategorySelected,
    selectedCount
  }
}
